import React from 'react';
import { Box, Paper, Button, CircularProgress } from '@mui/material';
import { Download as DownloadIcon } from '@mui/icons-material';
import { CopyRow } from './SamlConnectionDetails';
import {
  ConnectionSectionHeading,
  accentFor,
  panelPaperSx,
} from './ConnectionPanelSection';

/*
 * Connection details for a SAML custom application.
 *
 * This replaces an inline `label: value` table that predated the OIDC panel. The values are
 * unchanged; what changed is that they are now presented the same way OIDC presents its own,
 * because an admin should not have to learn two layouts for one feature.
 *
 * The per-application / per-realm split is the substantive part, and it mirrors OIDC exactly:
 *   - Client ID and Login URL are unique to this application.
 *   - The metadata descriptor is realm-wide and identical for every application in the tenant.
 * Those look alike as URLs, and an admin who hands a service provider the realm descriptor
 * believing it identifies their application gets a working import and a confusing failure
 * later. Saying which is which is the whole point of the two headings.
 */
const SamlConnectionPanel = ({
  app,
  copiedKey,
  onCopy,
  onDownloadMetadata,
  downloadingMetadata = false,
}) => {
  const accent = accentFor('saml');

  return (
    <Paper elevation={0} sx={panelPaperSx}>
      {/* ---- Per-client: unique to this application ---- */}
      <ConnectionSectionHeading
        title="This application"
        chipLabel="SAML 2.0"
        accent={accent.main}
        accentLight={accent.light}
        hint="Unique to this application. Nothing else in your tenant shares these values."
      />

      <CopyRow
        label="Client ID"
        hint="The entity ID this application identifies itself with. Prism matches the issuer on an incoming AuthnRequest against it exactly."
        value={app.clientId}
        copied={copiedKey === 'clientId'}
        onCopy={() => onCopy('clientId', app.clientId)}
      />

      <CopyRow
        label="Login URL"
        hint="Where the portal tile sends the user to start IdP-initiated sign-in for this application."
        value={app.loginUrl}
        copied={copiedKey === 'loginUrl'}
        onCopy={() => onCopy('loginUrl', app.loginUrl)}
      />

      {/* ---- Per-realm: identical for every application in the tenant ---- */}
      <Box sx={{ mt: 3 }}>
        <ConnectionSectionHeading
          title="Realm endpoints"
          accent={accent.main}
          accentLight={accent.light}
          hint="Identical for every application in your tenant — these describe Prism, not this application."
        />

        <CopyRow
          label="Metadata URL"
          hint="Prism's SAML descriptor: signing certificate and endpoints in one document. Most service providers can import this instead of having them entered by hand. Public and unauthenticated."
          value={app.metadataUrl}
          copied={copiedKey === 'metadataUrl'}
          onCopy={() => onCopy('metadataUrl', app.metadataUrl)}
          external
        />

        {/*
          Kept as a separate action rather than folded into CopyRow: copying the URL and
          downloading the document are different tasks, and service providers that cannot
          fetch a URL are exactly the ones that need the file.
        */}
        <Button
          size="small"
          variant="outlined"
          startIcon={
            downloadingMetadata ? <CircularProgress size={14} /> : <DownloadIcon />
          }
          onClick={onDownloadMetadata}
          disabled={downloadingMetadata || !app.metadataUrl}
          sx={{
            color: accent.main,
            borderColor: `${accent.main}40`,
            textTransform: 'none',
            fontFamily: 'Metropolis, sans-serif',
            fontSize: '0.75rem',
            whiteSpace: 'nowrap',
            '&:hover': { borderColor: accent.main, backgroundColor: accent.light },
          }}
        >
          Download XML
        </Button>
      </Box>
    </Paper>
  );
};

export default SamlConnectionPanel;
